import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import TagManager from "react-gtm-module";

const ThankYou = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Track page view with Google Tag Manager
    TagManager.dataLayer({
      dataLayer: {
        event: "page_view",
        page_title: "Thank You - Free Study Guide",
        page_location: window.location.href,
      },
    });
  }, []);

  const handleBackToHome = () => {
    // Determine the source page based on where the form was submitted from
    const sourcePage = location.state?.from || "/exam-prep";
    navigate(sourcePage);
  };

  const getBackButtonText = () => {
    const sourcePage = location.state?.from || "/exam-prep";
    if (sourcePage === "/exam-prep/nurses") {
      return "← Back to Nurses Exam Prep";
    }
    if (sourcePage === "/fluency-challenge") {
      return "← Back to Fluency Challenge";
    }
    if (sourcePage === "/exam-prep/scholarships") {
      return "← Back to Scholarships";
    }
    return "← Back to Exam Prep";
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F0FDF1] px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl rounded-3xl bg-white p-8 text-center shadow-2xl"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6 flex justify-center"
        >
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E9F7EF]">
            <CheckCircle className="h-12 w-12 text-[#14B82C]" />
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h1 className="mb-4 text-4xl font-bold text-[#042F0C]">
            Success! Your Free Study Guide is Waiting.
          </h1>

          <p className="mb-8 text-xl leading-relaxed text-[#3D3D3D]">
            You've unlocked a complimentary language exam study guide! Attend
            your free consultation to claim it. We'll email you shortly to
            schedule—don't forget to check your spam folder!
          </p>

          {/* Next Steps Section */}
          <div className="mb-8 rounded-2xl bg-[#FFFFEA] p-6 text-left">
            <h2 className="mb-4 text-xl font-semibold text-[#042F0C]">
              Next Steps
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#14B82C]">
                  <span className="text-sm font-bold text-white">1</span>
                </div>
                <p className="text-[#3D3D3D]">
                  Check your email for scheduling details
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#14B82C]">
                  <span className="text-sm font-bold text-white">2</span>
                </div>
                <p className="text-[#3D3D3D]">
                  Attend your free consultation call
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#14B82C]">
                  <span className="text-sm font-bold text-white">3</span>
                </div>
                <p className="text-[#3D3D3D]">
                  Receive your personalized study guide
                </p>
              </div>
            </div>
          </div>

          {/* Back to Home Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBackToHome}
            className="rounded-full bg-[#14B82C] px-8 py-3 text-lg font-semibold text-white transition-colors hover:bg-green-700"
          >
            {getBackButtonText()}
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ThankYou;
